import {createSelector} from 'reselect'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'

import {ApiRequestStates} from '../../const'
import {tagsByCategory} from './enums'
import * as plannerViewSelectors from './plannerView'

const categoryView = (state) => get(state, 'lookbuilder.categoryView')

export const currentCategory = (state) => get(categoryView(state), 'currentCategory')
export const productsBySlug = (state) => get(categoryView(state), 'productsBySlug')
export const allProductSlugs = (state) => get(categoryView(state), 'allProductSlugs')
export const previousCategory = (state) => get(categoryView(state), 'previousCategory')
export const fetchProductsByCategoryStatus = (state) =>
  get(categoryView(state), 'fetchProductsByCategoryStatus')

/**
 * Checks if we're fetching products in order to display appropriate UI
 * @returns {Boolean}
 */
export const isFetchProductsByCategoryPending = createSelector(
  fetchProductsByCategoryStatus,
  ($fetchProductsByCategoryStatus) => $fetchProductsByCategoryStatus === ApiRequestStates.PENDING,
)

/**
 * To display the Category View page, we need our products arranged via subcategory.
 * These subcategories come from the product service in the form of a `tags` array,
 * which can include tags other than the subcategories we're looking for.
 *
 * @returns {Object} Keys are subcategory slugs, values are arrays of product objects
 */
export const allProductsBySubcategory = createSelector(
  currentCategory,
  productsBySlug,
  ($currentCategory, $productsBySlug) => {
    const subcategories = tagsByCategory[$currentCategory] || []
    const products = Object.values($productsBySlug)
    return subcategories.reduce(
      (output, subcategory) => ({
        ...output,
        [subcategory]: products.filter((product) => product.subcategories.includes(subcategory)),
      }),
      {},
    )
  },
)

/**
 * Checks if the user has added a product in the Planner View to the current
 * category in the Category View and returns it if available.
 *
 * @returns {Object|undefined} The currently select product in this category
 */
export const currentlySelectedProductInCategoryView = createSelector(
  currentCategory,
  plannerViewSelectors.productsByCategory,
  ($currentCategory, plannerViewProductsByCategory) => {
    const product = plannerViewProductsByCategory[$currentCategory]
    return isEmpty(product) ? undefined : product
  },
)
